import { Pressable } from '@atlaskit/primitives/compiled';
import { fg } from '@confluence/feature-gating';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { css } from '@compiled/react';
import memoizeOne from 'memoize-one';
import React, { PureComponent, type CSSProperties } from 'react';
import type { IntlShape, WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import window from 'window-or-global';
import { token } from '@atlaskit/tokens';

import { DataSourceProvider as ReferentialityDataSourceProvider } from '@atlassian/editor-referentiality';

import type { DocNode } from '@atlaskit/adf-schema';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { EmojiResource } from '@atlaskit/emoji/resource';
import { ReactRenderer as AkRenderer } from '@atlaskit/renderer';
import { EditorCardProvider } from '@atlaskit/editor-card-provider';
import type { ObjectKey, TaskState } from '@atlaskit/task-decision';
import type ProfileClient from '@atlaskit/profilecard/client';
import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { cssMap } from '@atlaskit/css';

import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd';
import { ContentRefetchHandler } from '@confluence/content-body/entry-points/ContentRefetchHandler';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers';
import type { RendererExtensionHandlers } from '@confluence/fabric-extension-lib/entry-points/extensionTypes';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { createSmartCardEventHandler } from '@confluence/adf-renderer/entry-points/createSmartCardEventHandler';
import {
	registerGlobalMediaViewedListener,
	removeGlobalMediaViewedListener,
} from '@confluence/adf-renderer/entry-points/AttachmentEvents';
import { ANALYTICS_EVENT_SEVERITY_TRACKING } from '@confluence/adf-renderer/entry-points/ADFRendererComponent';
import { FloatingBannerStateContainer } from '@confluence/banners/entry-points/FloatingBannerStateContainer';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer';
import type { TaskAction } from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider';
import type { ProfileCardGetActions } from '@confluence/fabric-media-support/entry-points/ProfileCardProvider';
import { getMediaFeatureFlags } from '@confluence/fabric-media-support/entry-points/mediaFeatureFlags';
import {
	SetBatchedTaskStatusMutation,
	TaskBatcher,
	ConfluenceTaskDecisionProvider,
} from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider';
import { getProfileCardProvider } from '@confluence/fabric-media-support/entry-points/ProfileCardProvider';
import { createMentionsConfig } from '@confluence/fabric-media-support/entry-points/MentionsProvider';
import { createRendererTemplateMediaProvider } from '@confluence/fabric-media-support/entry-points/TemplateMediaProvider';
import { createRendererMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider';
import { getCanViewUserProfile } from '@confluence/global-operations/entry-points/getCanViewUserProfile';
import { WithAnnotationsWrapper } from '@confluence/content-renderer/entry-points/WithAnnotationsWrapper';
import { inlineCommentStyles, messages } from '@confluence/content-renderer/entry-points/styled';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context';
import { getAtlassianPeopleProfileUrl } from '@confluence/profile/entry-points/helpers';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import type { RoutesContextType } from '@confluence/route-manager/entry-points/RoutesContext';
import type { WithNavigationPolicyProps } from '@confluence/route-manager/entry-points/useNavigationPolicy';
import { withRoutesContext } from '@confluence/route-manager/entry-points/withRoutesContext';
import { withNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy';

import type { CoreInvitesContextType } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext';
import { withCoreInvitesContext } from '@confluence/core-invites-provider/entry-points/withCoreInvitesContext';
import type { ExternalShareContextType } from '@confluence/external-share-context/entry-points/ExternalShareContext';
import { withExternalShareContext } from '@confluence/external-share-context/entry-points/withExternalShareContext';
import type {
	FeatureFlagsType,
	SessionDataType,
} from '@confluence/session-data/entry-points/SessionDataTypes';
import type { SessionDataContextInjected } from '@confluence/session-data/entry-points/withSessionData';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery';
import { withSessionData } from '@confluence/session-data/entry-points/withSessionData';

import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { PageLoadMark } from '@confluence/browser-metrics/entry-points/PageLoadMark';
import { RendererAnalyticsListener } from '@confluence/fabric-analytics-listeners/entry-points/RendererAnalyticsListener';
import { getConnectDataListener } from '@confluence/app-referentiality/entry-points/connectDataListener';
import { expVal } from '@confluence/feature-experiments';

import { getExtensionHandlers } from '../../lib/renderer-extensions';

import { unsupportedContentLevelsTrackingOptions } from './fabric-feature-flags';
import type { ExternalProps } from './Renderer';
import { populateExtensionProviders } from './populateExtensionProviders';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- To migrate as part of go/ui-styling-standard
const StyledRenderer = styled.div<{
	intl: IntlShape;
}>`
	${({ intl }) => inlineCommentStyles(intl)}
`;

const inlineCommentStylesCompiled = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div[mode="stick"]': {
		zIndex: 1,
		background: token('color.background.inverse.subtle'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&.inline-highlight': {
			cursor: 'pointer',
			backgroundColor: token('color.background.accent.yellow.subtler'),
			borderBottom: `2px solid ${token('color.border.accent.yellow')}`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.active-highlight': {
				backgroundColor: token('color.background.accent.yellow.subtle'),
				borderBottom: `2px solid ${token('color.border.accent.yellow')}`,
				boxShadow: token('elevation.shadow.overlay'),
			},

			/* Accessibility markers for screen readers: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/mark#accessibility_concerns */
			'&::before, &::after': {
				clipPath: 'inset(100%)',
				clip: 'rect(1px, 1px, 1px, 1px)',
				height: '1px',
				overflow: 'hidden',
				position: 'absolute',
				whiteSpace: 'nowrap',
				width: '1px',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.first-mark-highlight::before': {
				content: 'var(--content-renderer-inline-comment-marker-start)',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.last-mark-highlight::after': {
				content: 'var(--content-renderer-inline-comment-marker-end)',
			},
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&.inline-create-comment-selection': {
			backgroundColor: token('color.background.accent.yellow.subtle'),
			borderBottom: `2px solid ${token('color.border.accent.yellow')}`,
			boxShadow: token('elevation.shadow.overlay'),
		},
	},
});

const styles = cssMap({
	renderContentButton: {
		backgroundColor: token('color.background.neutral.subtle'),
	},
});

const _RERENDER_STATE_NONE = '';
const _RERENDER_STATE_REQUESTED = 'requested';

export type ComponentProps = ExternalProps;

type ProfilecardProvider = {
	cloudId: string;
	resourceClient: ProfileClient;
	getActions: ProfileCardGetActions;
};

type ComponentState = {
	_root: HTMLElement | undefined;
	rerender: string;
	taskBatcher: TaskBatcher | undefined;
	shouldRefetchContent: boolean;
};

type ExcerptSpecificProps = {
	mediaToken?: string;
};

class RendererComponent extends PureComponent<
	ComponentProps &
		WrappedComponentProps &
		RoutesContextType &
		WithNavigationPolicyProps &
		CoreInvitesContextType &
		ExternalShareContextType &
		SessionDataContextInjected & { offsetTop?: number } & ExcerptSpecificProps,
	ComponentState
> {
	state = {
		_root: undefined,
		rerender: _RERENDER_STATE_NONE,
		referentialityDataSourceProvider: new ReferentialityDataSourceProvider(),
		taskBatcher: undefined as TaskBatcher | undefined,
		shouldRefetchContent: false,
	};

	_handleActionToggle = (objectKey: ObjectKey, state: TaskState) => {
		// add the task action to be batched/updated in the batch before firing the request
		this.state.taskBatcher?.add({ objectKey, state });
	};

	_onBatchTaskUpdate = (batch: TaskAction[]) => {
		const { objectId, isPreviewMode } = this.props;

		const updateBatch = batch.map((update) => {
			const { objectKey, state } = update;
			const status = state === 'DONE' ? 'CHECKED' : 'UNCHECKED';

			return { taskId: objectKey.localId, status };
		});

		const taskBatch = {
			contentId: objectId,
			tasks: updateBatch,
			trigger: 'VIEW_PAGE',
		};

		if (!isPreviewMode) {
			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			getApolloClient()
				.mutate({
					mutation: SetBatchedTaskStatusMutation,
					variables: taskBatch,
				})
				.then(() => {
					this.setState({ shouldRefetchContent: true });
				})
				.catch((error: Error): any => {
					void getAnalyticsWebClient().then((client) => {
						client.sendOperationalEvent({
							source: 'page',
							action: 'batchTaskUpdate',
							actionSubject: 'checkbox',
							attributes: {
								error: error.message || String(error),
								stack: error.stack,
							},
						});
					});
				});
		}
	};

	_handleProfileCardActions: ProfileCardGetActions = (userId) => {
		const { push } = this.props;
		const atlassianPeopleProfileUrl = getAtlassianPeopleProfileUrl(userId, null, CONTEXT_PATH);
		return [
			{
				label: this.props.intl.formatMessage({ id: 'view.profile' }),
				link: atlassianPeopleProfileUrl,
				callback: () => {
					push(getAtlassianPeopleProfileUrl(userId, null, CONTEXT_PATH));
				},
			},
		];
	};

	componentDidMount() {
		const { adf, sessionData } = this.props;

		if (adf && sessionData?.isLicensed) {
			registerGlobalMediaViewedListener();
		}

		getConnectDataListener().init({
			emit: (localId, data) =>
				this.state.referentialityDataSourceProvider.createOrUpdate(localId, data),
		});
		const taskBatcher = new TaskBatcher(
			expVal('confluence_frontend_task_batching_interval', 'value', 3000) as number,
		);
		taskBatcher.onFlush(this._onBatchTaskUpdate);

		this.setState({ taskBatcher });
	}

	/**
	 * Needed to force an update of Atlaskit Renderer to wake-up TWP code inside that opens expand based on hash in the case where it hasn't changed.
	 * The alternative of using getDerivedStateFromProps required multiple steps which didn't work with RTL testing.
	 * Settled on normally discouraged setState inside of componentDidUpdate with an eslint-disable to get through Bamboo.
	 */
	/**
	 * When the objectId of the renderer changes, we want to reset the `shouldFetchContent` state variable so we don't
	 * cause unnecessary refetches; Calling setState behind a condition that will only happen on page transitions
	 */
	componentDidUpdate(prevProps) {
		const { objectId } = this.props;
		const { rerender } = this.state;
		if (rerender === _RERENDER_STATE_REQUESTED) {
			// eslint-disable-next-line
			this.setState({ rerender: _RERENDER_STATE_NONE });
		}

		if (objectId !== prevProps.objectId) {
			// eslint-disable-next-line
			this.setState({ shouldRefetchContent: false });
		}
	}

	componentWillUnmount() {
		const { adf, sessionData } = this.props;

		if (adf && sessionData?.isLicensed) {
			removeGlobalMediaViewedListener();
		}
	}

	getExpandHash = () => {
		if (this.state.rerender === _RERENDER_STATE_REQUESTED) {
			return '';
		} else {
			return this.props.getHash();
		}
	};

	getExtensionHandlers = (shouldIncludeMacroHandlers?: boolean): RendererExtensionHandlers => {
		const {
			adf,
			objectId,
			spaceKey,
			showTemplateVariablesInPreview,
			showTemplateInputInPreview,
			appearance,
			isPreviewMode = false,
		} = this.props;

		const { referentialityDataSourceProvider } = this.state;

		return getExtensionHandlers({
			adf,
			contentId: objectId,
			appearance: appearance as string,
			showTemplateVariablesInPreview,
			showTemplateInputInPreview,
			spaceKey,
			shouldIncludeMacroHandlers,
			shouldIncludeMacroWithReferencesHandlers: true,
			referentialityDataSourceProvider,
			fromFabricEditor: true,
			isPreviewMode,
		});
	};

	getEventHandlers = (): React.ComponentProps<typeof AkRenderer>['eventHandlers'] => {
		const { matchRoute, push } = this.props;

		return {
			...createSmartCardEventHandler({ matchRoute, push }),
		};
	};

	_setRoot = (ref) => this.setState({ _root: ref });

	/* getting type issues with ProfilecardProvider in atlaskit with ProfileCardProvider from next.
   This helps resolve the type errors */
	getProfilecardProvider = (
		cloudId: string,
		getActions: ProfileCardGetActions,
		featureFlags: FeatureFlagsType,
	): Promise<ProfilecardProvider> => {
		return getProfileCardProvider(cloudId, getActions, featureFlags);
	};

	getProviders = memoizeOne((flags) => {
		const {
			intl,
			accountId,
			contentType,
			cloudId,
			environment,
			objectId,
			spaceKey,
			isExternalShareRequest,
			sessionData,
			showTemplateInputInPreview,
			showTemplateVariablesInPreview,
			openCoreInvites,
			userRole,
			mediaToken,
			shareToken,
		} = this.props;

		const { featureFlags, activationId } = sessionData as SessionDataType;

		const isTemplateMedia = showTemplateVariablesInPreview || showTemplateInputInPreview;
		const mediaProvider = isTemplateMedia
			? createRendererTemplateMediaProvider({
					spaceKey,
					templateIds: [objectId],
				})
			: createRendererMediaProvider({
					contentId: objectId,
					spaceKey,
					isExternalShare: isExternalShareRequest,
					mediaToken,
					shareToken,
				});
		const providerFactory = ProviderFactory.create({
			mediaProvider,
			taskDecisionProvider: Promise.resolve(
				new ConfluenceTaskDecisionProvider(intl, flags, objectId, this._handleActionToggle),
			),
			cardProvider: Promise.resolve(new EditorCardProvider(undefined, undefined, 'CONFLUENCE')),
		});

		if (cloudId && accountId) {
			providerFactory.setProvider(
				'mentionProvider',
				createMentionsConfig(cloudId, accountId, undefined, undefined, openCoreInvites, userRole)
					.then((config) => config.mentionResource)
					.catch((err) => {
						if (process.env.NODE_ENV !== 'production') {
							// eslint-disable-next-line no-console
							console.error('Failed to initialise mention resource ', err);
						}
						return Promise.reject();
					}),
			);
		}

		const hasAccess =
			sessionData?.accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			sessionData?.accessStatus === AccessStatus.LICENSED_USE_ACCESS;

		if (cloudId) {
			providerFactory.setProvider(
				'emojiProvider',
				getEmojiProvider(EmojiResource, {
					cloudId,
					userId: accountId,
					allowAnonymousAccess: true,
				}),
			);
		}

		if (cloudId && hasAccess) {
			// If global view user profile is enabled then user can view profile card
			// Making promise void as in the worst case the
			void getCanViewUserProfile().then((hasViewUserProfilePermission) => {
				if (hasViewUserProfilePermission) {
					providerFactory.setProvider(
						'profilecardProvider',
						this.getProfilecardProvider(
							cloudId,
							this._handleProfileCardActions,
							featureFlags,
						) as any,
					);
				}
			});
		}

		if (isTemplateMedia) {
			const contextIdentifierProvider = async () => {
				const templateObjectId = JSON.stringify({
					templateId: objectId,
					spaceKey,
				});

				return Promise.resolve({
					containerId: spaceKey || '',
					objectId: templateObjectId,
					product: 'confluence',
				});
			};
			providerFactory.setProvider('contextIdentifierProvider', contextIdentifierProvider());
		} else if (spaceKey) {
			providerFactory.setProvider(
				'contextIdentifierProvider',
				Promise.resolve({
					containerId: spaceKey,
					objectId,
					product: 'confluence',
				}),
			);
		}

		populateExtensionProviders(providerFactory, {
			intl,
			contentId: objectId,
			containerId: spaceKey || '',
			accountId,
			cloudId,
			spaceKey,
			contentType,
			environment,
			featureFlags,
			extensionHandlers: this.getExtensionHandlers(true),
			showTemplateInputInPreview,
			showTemplateVariablesInPreview,
			activationId,
		});

		return providerFactory;
	});

	decodeHash = (hash) => {
		let decodedHash = '';
		try {
			// try to decode the hash
			decodedHash = decodeURIComponent(hash);
		} catch (e) {
			// if hash is not encoded, it will throw a URI Malformed error,
			// which we will then return the original hash
			decodedHash = hash;
		}
		return decodedHash;
	};

	handleClicksInRenderer = (event) => {
		/**
		 * Covering a corner case:
		 * If the user
		 * - has followed a deep link to a header inside an expand
		 * - then closes that expand
		 * - then clicks a TOC link to the same header
		 * the renderer code will not see a change in the deep link has, so it won't run which leaves the user scrolled
		 * to a closed expand element.
		 * This code detects the above situation and sets the rerender state property to begin a double re-render of the
		 * renderer (once with no hash and once with the original hash). This lets the renderer code see a change so it
		 * opens the expand.
		 */
		const deepLinkHash = event.target.hash;
		if (deepLinkHash === `#${this.getExpandHash()}`) {
			const tocTarget = document.getElementById(this.decodeHash(this.getExpandHash()));
			if (tocTarget) {
				const expandParent = tocTarget.closest("[data-node-type='expand']");
				// If the TOC link points to a header inside a closed expand
				if (expandParent && expandParent.getAttribute('data-expanded') === 'false') {
					this.setState({
						rerender: _RERENDER_STATE_REQUESTED,
					});
				}
			}
		}
	};

	renderRenderer = (extensionHandlers, dataProviders) => {
		const {
			adf,
			id,
			className,
			style,
			allowInlineComments,
			ignoreHeadingAnchorLinksFeatureFlag,
			renderWhenReady,
			onComplete,
			objectId,
			sessionData,
			allowPlaceholderText = false,
			annotationProvider,
			allowAnnotations = false,
			UNSTABLE_allowTableAlignment = true,
			UNSTABLE_allowTableResizing = true,
		} = this.props;

		const { shouldRefetchContent } = this.state;

		if (!adf) {
			return null;
		}

		// Allow disabling the feature for previews.
		const allowHeadingAnchorLinks = !ignoreHeadingAnchorLinksFeatureFlag;

		const { offsetTop, appearance } = this.props;
		const stickyHeaders = offsetTop !== undefined && {
			offsetTop,
		};

		const { featureFlags, isAdminHubAIEnabled } = sessionData as SessionDataType;

		// used for renderer's annotation provider
		const localRef = React.createRef() as React.RefObject<HTMLDivElement>;

		const shouldSSRsmartLinks =
			isPageSSRd() &&
			!isEmbeddedConfluence_DO_NOT_USE() &&
			fg('confluence_frontend_fix_smart_link_flickering');

		const content = (
			<RendererAnalyticsListener objectId={objectId}>
				{(appearance === 'full-page' || appearance === 'full-width') && (
					<PageLoadMark name="onBeforeAkRenderer" />
				)}
				{/**
				 * If you modify any of the AkRenderer props below, please also
				 * update `next/packages/adf-renderer/src/ADFRenderer.tsx`
				 * to maintain parity with the next version of the renderer.
				 */}
				<WithAnnotationsWrapper
					annotationProvider={annotationProvider}
					rendererRef={localRef}
					adfDocument={(this.props.navigationPolicy?.shimAdfUrls(adf) ?? adf) as DocNode}
					updateDocument={() => {}} /* unnecessary right now */
					isNestedRender
				>
					<AkRenderer
						appearance={this.props.appearance}
						contentMode={this.props.contentMode}
						document={(this.props.navigationPolicy?.shimAdfUrls(adf) ?? adf) as DocNode}
						adfStage="stage0"
						dataProviders={dataProviders}
						eventHandlers={this.getEventHandlers()}
						extensionHandlers={extensionHandlers}
						featureFlags={{}}
						portal={this.state._root}
						onComplete={onComplete}
						allowColumnSorting
						allowHeadingAnchorLinks={{
							allowNestedHeaderLinks: allowHeadingAnchorLinks,
							activeHeadingId: this.decodeHash(this.getExpandHash()),
						}}
						allowAltTextOnImages
						media={{
							allowLinking: true,
							allowCaptions: true,
							featureFlags: getMediaFeatureFlags(featureFlags),
						}}
						stickyHeaders={stickyHeaders}
						useSpecBasedValidator
						allowCopyToClipboard
						allowWrapCodeBlock
						analyticsEventSeverityTracking={ANALYTICS_EVENT_SEVERITY_TRACKING}
						allowSelectAllTrap
						unsupportedContentLevelsTracking={unsupportedContentLevelsTrackingOptions()}
						allowPlaceholderText={allowPlaceholderText}
						allowCustomPanels
						smartLinks={{ hideHoverPreview: false, ssr: shouldSSRsmartLinks }}
						allowAnnotations={allowAnnotations}
						annotationProvider={annotationProvider}
						UNSTABLE_allowTableAlignment={UNSTABLE_allowTableAlignment}
						UNSTABLE_allowTableResizing={UNSTABLE_allowTableResizing}
					/>
				</WithAnnotationsWrapper>
			</RendererAnalyticsListener>
		);

		const rendererContent = fg('custom-interactive-elements-not-keyboard-focusable') ? (
			<Pressable xcss={styles.renderContentButton} onClick={this.handleClicksInRenderer}>
				{content}
			</Pressable>
		) : (
			// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-static-element-interactions, @atlassian/a11y/interactive-element-not-keyboard-focusable
			<div onClick={this.handleClicksInRenderer}>{content}</div>
		);

		return fg('confluence_frontend_cc-fabric-editor_compiled') ? (
			<div
				id={id}
				css={inlineCommentStylesCompiled}
				data-inline-comments-target={allowInlineComments ? 'true' : 'false'}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className={className}
				ref={this._setRoot}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				style={
					{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						...style,
						'--content-renderer-inline-comment-marker-start': this.props.intl.formatMessage(
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
							messages.contentRendererInlineCommentMarkerStart,
						),
						'--content-renderer-inline-comment-marker-end': this.props.intl.formatMessage(
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
							messages.contentRendererInlineCommentMarkerEnd,
						),
					} as CSSProperties
				}
				data-test-appearance={this.props.appearance}
			>
				<ConfluenceSmartCardProvider
					{...getSmartCardRenderers()}
					isAdminHubAIEnabled={isAdminHubAIEnabled}
				>
					{rendererContent}
				</ConfluenceSmartCardProvider>
				{renderWhenReady && renderWhenReady()}
				<ContentRefetchHandler contentId={objectId} shouldRefetch={shouldRefetchContent} />
			</div>
		) : (
			<StyledRenderer
				id={id}
				intl={this.props.intl}
				data-inline-comments-target={allowInlineComments ? 'true' : 'false'}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={className}
				ref={this._setRoot}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{ ...style }}
				data-test-appearance={this.props.appearance}
			>
				<ConfluenceSmartCardProvider
					{...getSmartCardRenderers()}
					isAdminHubAIEnabled={isAdminHubAIEnabled}
				>
					{rendererContent}
				</ConfluenceSmartCardProvider>
				{renderWhenReady && renderWhenReady()}
				<ContentRefetchHandler contentId={objectId} shouldRefetch={shouldRefetchContent} />
			</StyledRenderer>
		);
	};

	render() {
		window.performance.mark('CFP-63.view.page.classic.renderer.rendered');

		return (
			<Subscribe to={[FlagsStateContainer]}>
				{(flags: FlagsStateContainer) => {
					const dataProviders = this.getProviders(flags);

					return this.renderRenderer(this.getExtensionHandlers(false), dataProviders);
				}}
			</Subscribe>
		);
	}
}

export const RendererComponentWithIntl = injectIntl(withNavigationPolicy(RendererComponent));

class RendererComponentWithBanner extends PureComponent<
	ComponentProps &
		RoutesContextType &
		CoreInvitesContextType &
		ExternalShareContextType &
		SessionDataContextInjected,
	ComponentState
> {
	render() {
		const { allowStickyHeaders } = this.props;

		if (allowStickyHeaders === false) {
			return <RendererComponentWithIntl {...this.props} />;
		}

		return (
			<Subscribe to={[BannerStateContainer, FloatingBannerStateContainer]}>
				{(bannerState: BannerStateContainer, floatingBanners: FloatingBannerStateContainer) => {
					const offsetTop = bannerState.getTotalHeight() + floatingBanners.getFloatingHeight();
					return <RendererComponentWithIntl offsetTop={offsetTop} {...this.props} />;
				}}
			</Subscribe>
		);
	}
}

export default withErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(
	withCoreInvitesContext(
		withExternalShareContext(withSessionData(withRoutesContext(RendererComponentWithBanner))),
	),
);
